import React, { useEffect, useRef } from 'react';
import Dialog from '@material-ui/core/Dialog';
import Grid from '@material-ui/core/Grid';
import DialogContent from '@material-ui/core/DialogContent';
import Timeline from '@material-ui/lab/Timeline';
import TimelineItem from '@material-ui/lab/TimelineItem';
import TimelineSeparator from '@material-ui/lab/TimelineSeparator';
import TimelineConnector from '@material-ui/lab/TimelineConnector';
import TimelineContent from '@material-ui/lab/TimelineContent';
import TimelineDot from '@material-ui/lab/TimelineDot';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import CircularProgress from '@material-ui/core/CircularProgress';
import Paper from '@material-ui/core/Paper';
import { addMinutes, format, parseISO } from 'date-fns';
import { format as newFormat } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';
import useStyles from './styles';
import ApiService from '../../../services/ApiService';
import RemoveStatusPopUp from './RemoveStatusPopUp/RemoveStatusPopUp';

function HistoricPopUp(props) {
  const { selectedTicketNumber, historicOpened, editMode } = props;
  const classes = useStyles();
  const [historic, setHistoric] = React.useState([]);
  const [historicActiveLength, setHistoricActiveLength] = React.useState(0);
  const [openCancelPopUp, setOpenCancelRemoveStatus] = React.useState(false);
  const [statusToDelete, setStatusToDelete] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const mountedRef = useRef(true);
  const { t } = useTranslation();
  const otherStatuses = [
    'Anomaly added',
    'Anomaly modified',
    'Unplanned -> Planned',
    'Planned -> Unplanned',
  ];

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  function resetHistoricActiveLength(historicUpdated) {
    const activeHistoric = historicUpdated.filter(
      (status) => status.active === true,
    );
    setHistoricActiveLength(activeHistoric.length);
  }

  async function getHistoric(ticketNumber) {
    const result = await ApiService.getHistoric(ticketNumber);
    setHistoric(result.data);
    resetHistoricActiveLength(result.data);
  }

  useEffect(() => {
    if (selectedTicketNumber !== '' && mountedRef.current) {
      getHistoric(selectedTicketNumber);
    }
  }, [selectedTicketNumber]);

  const handleClose = () => {
    if (mountedRef.current) {
      props.setPopUpOpenedState(false);
    }
  };

  function transferDateFormatWithHours(date) {
    const cest = addMinutes(parseISO(date), 120);
    const cestTime = newFormat(cest, 'dd/MM/yyyy - H:mm');
    return cestTime;
  }

  function transferDateFormat(date) {
    const newDate = new Date(date);
    return format(newDate, 'dd/MM/yyyy');
  }

  function replaceString(oldString) {
    if (oldString.includes('anomaly added')) {
      return oldString.split('anomaly added').join(t('anomaly added'));
    }
    if (oldString.includes('anomalies added')) {
      return oldString.split('anomalies added').join(t('anomalies added'));
    }
    return t(oldString);
  }

  function deleteStatus(status) {
    setStatusToDelete(status);
    setOpenCancelRemoveStatus(true);
  }

  function validateDeletingStatus() {
    setOpenCancelRemoveStatus(false);
    setLoading(true);
    ApiService.deleteStatus(selectedTicketNumber, statusToDelete)
      .then((response) => {
        const historicModified = historic.slice();
        const indexToDelete = historicModified.indexOf(statusToDelete);
        statusToDelete.active = false;
        historicModified.splice(indexToDelete, 1, statusToDelete);
        setHistoric(historicModified);
        resetHistoricActiveLength(historicModified);
        setStatusToDelete(null);
        setLoading(false);
        if (response.data.length > 0) {
          props.setTicketStatus(response.data[0].status);
        }
      })
      .catch(() => {
        setStatusToDelete(null);
        setLoading(false);
      });
  }

  return (
    <div>
      {loading && (
        <Paper
          elevation={0}
          position="absolute"
          zindex="2"
          data-testid="loader"
        >
          <CircularProgress className={classes.loader} />
        </Paper>
      )}
      <Dialog
        data-testid="historic-popup"
        open={historicOpened}
        onClose={handleClose}
        classes={{ paper: classes.paper }}
      >
        <DialogContent className={classes.dialogContent}>
          <Timeline align="left">
            {historic.map((histo, index) => {
              const historicId = `historic-${index}`;
              return (
                historic.length > 0 && (
                  <Grid container key={historicId}>
                    <Grid item xs={editMode ? 1 : 'auto'}>
                      {editMode &&
                        historicActiveLength > 1 &&
                        histo.active &&
                        index !== historic.length - 1 &&
                        !otherStatuses.includes(histo.status) && (
                          <IconButton
                            key={historicId}
                            aria-label="delete-status"
                            data-testid="delete-status"
                            className={classes.removeButton}
                            onClick={() => deleteStatus(histo)}
                          >
                            <RemoveCircleOutlineIcon />
                          </IconButton>
                        )}
                    </Grid>
                    <Grid item xs={editMode ? 11 : 12}>
                      <TimelineItem
                        key={historicId}
                        className={classes.missingOppositeContent}
                      >
                        <TimelineSeparator>
                          <TimelineDot
                            className={
                              histo.active
                                ? index === 0
                                  ? classes.timelineDotOutline
                                  : classes.timelineDotContained
                                : classes.timelineDisabled
                            }
                          />
                          <TimelineConnector
                            className={classes.timelineConnector}
                          />
                        </TimelineSeparator>
                        <TimelineContent>
                          <Typography className={classes.historicTitle}>
                            {histo.status ? t(histo.status) : 'N.A.'}
                            {' - '}
                            {histo.statusEffectiveDate
                              ? transferDateFormat(histo.statusEffectiveDate)
                              : 'N.A.'}
                            {' ('}
                            {t('Effective Date')}
                            {') '}
                            {!histo.active && ' - '}{' '}
                            {!histo.active && t('deleted')}
                          </Typography>
                          <div className={classes.container}>
                            <Typography className={classes.historicSubtitle}>
                              {t('modified by')}{' '}
                              {histo.reporter ? histo.reporter : 'N.A.'}{' '}
                              {t('the')}{' '}
                              {histo.startDate
                                ? transferDateFormatWithHours(histo.startDate)
                                : 'N.A.'}
                            </Typography>
                          </div>
                          <Typography className={classes.historicDescription}>
                            {histo.description
                              ? replaceString(histo.description)
                              : t('No description')}
                          </Typography>
                        </TimelineContent>
                      </TimelineItem>
                    </Grid>
                  </Grid>
                )
              );
            })}
          </Timeline>
        </DialogContent>
      </Dialog>
      <RemoveStatusPopUp
        openCancelPopUp={openCancelPopUp}
        confirmCancel={validateDeletingStatus}
        closeCancelPopUp={() => setOpenCancelRemoveStatus(false)}
      />
    </div>
  );
}
export default HistoricPopUp;
